"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VoyageTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const routes_1 = require("../../../../util/routes");
const common_styled_components_1 = require("../../../common-styled-components");
const compliance_1 = require("../../../../util/compliance/compliance");
const theme_1 = require("../../../../theme");
const lodash_1 = require("lodash");
const amountUtils_1 = require("../../../../util/amountUtils");
const timeUtils_1 = require("../../../../util/timeUtils");
const useSortedData_1 = require("../../../../hooks/useSortedData");
const Modal_1 = require("../Modal");
const react_1 = require("react");
const useEdit_1 = __importDefault(require("../../../../hooks/useEdit"));
const ExportCsvButton_1 = require("../ExportCsvButton");
const voyageTableColumns = (fuels, isInAdminTools) => [
    {
        key: "ship_name",
        label: "Ship",
        sortableKey: "ship_name",
    },
    {
        key: "start_date",
        label: "Date started",
        sortableKey: "start_date",
    },
    {
        key: "end_date",
        label: "Date ended",
        sortableKey: "end_date",
    },
    {
        key: "origin_name",
        label: "Origin",
        sortableKey: "origin_name",
    },
    {
        key: "destination_name",
        label: "Destination",
        sortableKey: "destination_name",
    },
    {
        key: "distance",
        label: "Distance (NM)",
        sortableKey: "distance",
    },
    {
        key: "type_id",
        label: "Type",
        sortableKey: "type_id",
    },
    {
        key: "intensity",
        label: "Intensity (gCO₂e/MJ)",
        sortableKey: "intensity.adjusted_intensity_gCO2e_per_MJ",
    },
    {
        key: "balance",
        label: "+/- (tCO₂e)",
        sortableKey: "intensity.adjusted_emission_balance_ton",
    },
]
    .concat(fuels.map((f) => ({
    key: f.fuel,
    label: f.name,
})))
    .concat(isInAdminTools
    ? {
        key: "tools",
        label: "",
    }
    : []);
const CellContent = ({ voyage, contentKey, metadata, removeVoyage, isInAdminTools, }) => {
    const compliance = (0, compliance_1.getCompliance)(voyage.intensity.adjusted_emission_balance_ton);
    if ((0, lodash_1.isNumber)(contentKey)) {
        if (voyage.fuels[contentKey]) {
            return (0, jsx_runtime_1.jsxs)("div", { children: [(0, amountUtils_1.formatNumber)(voyage.fuels[contentKey].amount), " t"] });
        }
        else {
            return (0, jsx_runtime_1.jsx)("div", {});
        }
    }
    const link = isInAdminTools
        ? `${routes_1.routeNames.admin}/${routes_1.routeNames.voyageAdmin}/${voyage.id}`
        : `${routes_1.routeNames.voyage}/${voyage.id}`;
    switch (contentKey) {
        case "start_date":
            return ((0, jsx_runtime_1.jsx)(common_styled_components_1.StrippedLink, { to: link, style: { color: theme_1.theme.colors.blue }, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: (0, timeUtils_1.formatDate)(new Date(voyage.start_date), true) }) }));
        case "end_date":
            return ((0, jsx_runtime_1.jsx)(common_styled_components_1.StrippedLink, { to: link, style: { color: theme_1.theme.colors.blue }, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: (0, timeUtils_1.formatDate)(new Date(voyage.end_date), true) }) }));
        case "type_id":
            return (0, jsx_runtime_1.jsx)("div", { children: voyage.type_id === 1 ? "Voyage" : "Port stay" });
        case "fuels":
            return ((0, jsx_runtime_1.jsx)("div", { children: (0, lodash_1.values)(voyage.fuels)
                    .map((fuel) => `${(0, amountUtils_1.formatNumber)(fuel.amount)}t ${metadata.fuels[fuel.fuel].name}`)
                    .join(", ") }));
        case "intensity":
            return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.ComplianceIcon, { src: compliance_1.complianceOptions[compliance].icon }), (0, amountUtils_1.formatNumber)(voyage.intensity.intensity_gCO2e_per_MJ, 2)] }));
        case "balance":
            return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [voyage.intensity.emission_balance_ton > 0 ? "+" : "", (0, amountUtils_1.formatNumber)(voyage.intensity.emission_balance_ton, 2)] }));
        case "destination_name":
            return ((0, jsx_runtime_1.jsx)("div", { children: voyage.type_id === 1 ? voyage.destination_name : (0, jsx_runtime_1.jsx)("div", {}) }));
        case "tools":
            return removeVoyage ? ((0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => removeVoyage(voyage), children: "Remove" }) })) : ((0, jsx_runtime_1.jsx)("div", {}));
        default:
            return (0, jsx_runtime_1.jsx)("div", { children: voyage[contentKey] });
    }
};
const VoyageTable = ({ voyages, metadata, isInAdminTools, refresh, }) => {
    const [modal, setModal] = (0, react_1.useState)({});
    const { sortedData, sortMethod, sortDirection, setSort } = (0, useSortedData_1.useSortedData)(voyages, "end_date", 1);
    const [editStatus, executeEdit] = (0, useEdit_1.default)("admin/voyage.json", refresh);
    const fuels = (0, lodash_1.chain)(voyages)
        .flatMap((v) => (0, lodash_1.values)(v.fuels).map((f) => f.fuel))
        .uniq()
        .sort()
        .map((f) => ({
        fuel: f,
        name: metadata.fuels[f]?.name ?? "Unknown fuel",
    }))
        .value();
    const removeVoyage = (voyage) => {
        setModal({
            type: "remove",
            value: voyage,
        });
    };
    const submit = (type, value) => {
        setModal({});
        if (type === "remove") {
            if (value.id !== undefined) {
                const data = {
                    id: value.id,
                };
                executeEdit(data, "DELETE");
            }
        }
    };
    const tableColumns = voyageTableColumns(fuels, isInAdminTools);
    const csvColumns = tableColumns
        .filter((column) => column.key !== "tools")
        .map((column) => ({
        header: column.label,
        getValue: (voyage) => {
            if ((0, lodash_1.isNumber)(column.key)) {
                return voyage.fuels[column.key]
                    ? `${(0, amountUtils_1.formatNumber)(voyage.fuels[column.key].amount) ?? ""} t`
                    : "";
            }
            switch (column.key) {
                case "start_date":
                    return (0, timeUtils_1.formatDate)(new Date(voyage.start_date), true);
                case "end_date":
                    return (0, timeUtils_1.formatDate)(new Date(voyage.end_date), true);
                case "type_id":
                    return voyage.type_id === 1 ? "Voyage" : "Port stay";
                case "intensity":
                    return ((0, amountUtils_1.formatNumber)(voyage.intensity.intensity_gCO2e_per_MJ, 2) ?? "");
                case "balance":
                    return `${voyage.intensity.emission_balance_ton > 0 ? "+" : ""}${(0, amountUtils_1.formatNumber)(voyage.intensity.emission_balance_ton, 2) ?? ""}`;
                case "destination_name":
                    return voyage.type_id === 1 ? voyage.destination_name : "";
                default: {
                    const value = voyage[String(column.key)];
                    return typeof value === "string" || typeof value === "number"
                        ? value
                        : "";
                }
            }
        },
    }));
    return (metadata && ((0, jsx_runtime_1.jsxs)("div", { children: [modal.type && ((0, jsx_runtime_1.jsx)(Modal_1.Modal, { type: "voyage", isOpen: modal.type !== undefined, closeModal: () => setModal({}), modalType: modal.type, value: modal.value, submit: submit })), (0, jsx_runtime_1.jsx)(common_styled_components_1.Row, { children: (0, jsx_runtime_1.jsx)(ExportCsvButton_1.ExportCsvButton, { rows: sortedData, columns: csvColumns, tableName: "voyages" }) }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: tableColumns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.SortableTH, { setSort: setSort, sortKey: c.sortableKey, sortDirection: sortMethod && sortMethod === c.sortableKey
                                    ? sortDirection
                                    : undefined, children: c.label }, c.key))) }) }), (0, jsx_runtime_1.jsx)("tbody", { children: sortedData.map((voyage) => {
                            return ((0, jsx_runtime_1.jsx)(common_styled_components_1.TR, { children: tableColumns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsx)(CellContent, { contentKey: c.key, voyage: voyage, isInAdminTools: isInAdminTools, metadata: metadata, removeVoyage: removeVoyage }) }, c.key))) }, voyage.id));
                        }) })] })] })));
};
exports.VoyageTable = VoyageTable;
